import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCardContext } from '../contexts/CardContext';
import { ApiService } from '../services/apiService';
import { CardStateService } from '../services/cardStateService';
import { CardState } from '../types/cardTypes';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Slider } from '@mui/material';
import { AppHeader } from '../components/AppHeader';
// @mcc-dev-preview:import
import { parseDevState } from '../dev/devPreview';
// @mcc-dev-preview:import
import { DEV_PREVIEW_ACTIVATED_CARD, DEV_PREVIEW_SELL_QUOTES } from '../dev/devPreviewMocks';
import './CardSell.css';

// @mcc-dev-preview:start card-sell-dev-state
type CardSellDevState = 'validating' | 'ready' | 'zero' | 'error' | 'dialog' | 'processing';

const CARD_SELL_DEV_STATES: CardSellDevState[] = [
  'validating',
  'ready',
  'zero',
  'error',
  'dialog',
  'processing',
];
// @mcc-dev-preview:end card-sell-dev-state

const CardSell: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cardData, cardId: contextCardId } = useCardContext();
  
  // Get cardId from context or URL parameters
  const finalCardId = contextCardId || searchParams.get('cardId');
  // @mcc-dev-preview:start card-sell-dev-state
  const devState = parseDevState(searchParams, CARD_SELL_DEV_STATES);
  // @mcc-dev-preview:end card-sell-dev-state

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingState, setIsValidatingState] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  
  // State for card and user data
  const [cardDetails, setCardDetails] = useState<any>(null);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [sellQuotes, setSellQuotes] = useState<any[]>([]);
  const [quoteCache, setQuoteCache] = useState<{ data: any; timestamp: number } | null>(null);
  const [maxSellCrypto, setMaxSellCrypto] = useState<number | null>(null);
  const [gasReserve, setGasReserve] = useState<number>(0);
  const [estimationDisclaimer, setEstimationDisclaimer] = useState<string | null>(null);
  
  // State for sell amount
  const [sellPercentage, setSellPercentage] = useState<number>(50);
  const [calculatedAmount, setCalculatedAmount] = useState<{ crypto: string; aud: string; processingFee: string; networkFee: string }>({ 
    crypto: '', 
    aud: '', 
    processingFee: '', 
    networkFee: '' 
  });

  // Dev / design: force UI states (playground-card-sell.html, scripts/dev-pages.ts)
  // @mcc-dev-preview:start card-sell-dev-state
  useEffect(() => {
    if (!devState) return;

    if (devState === 'validating') {
      return;
    }

    const pctParam = searchParams.get('sellPercentage');
    const pctFromParam = pctParam != null ? Number(pctParam) : NaN;

    setCardDetails(DEV_PREVIEW_ACTIVATED_CARD);
    setUserDetails({ id: 'dev-user' });
    setMaxSellCrypto(0.0125);
    setGasReserve(0.0001);
    setEstimationDisclaimer(
      'Maximum sell amount is estimated; the exact network fee is confirmed when you complete the transfer.'
    );
    setSellQuotes(DEV_PREVIEW_SELL_QUOTES);
    setIsValidatingState(false);

    switch (devState) {
      case 'ready':
        setSellPercentage(Number.isFinite(pctFromParam) ? pctFromParam : 50);
        break;
      case 'zero':
        setSellPercentage(0);
        break;
      case 'error':
        setSellPercentage(Number.isFinite(pctFromParam) ? pctFromParam : 75);
        setError(
          searchParams.get('errorMessage') ??
            'Failed to create sell order. Please try again.'
        );
        break;
      case 'dialog':
        setSellPercentage(Number.isFinite(pctFromParam) ? pctFromParam : 50);
        setOpenDialog(true);
        break;
      case 'processing':
        setSellPercentage(Number.isFinite(pctFromParam) ? pctFromParam : 50);
        setIsLoading(true);
        break;
      default:
        break;
    }
  }, [devState, searchParams]);
  // @mcc-dev-preview:end card-sell-dev-state

  // Validate card state and load data on page load
  useEffect(() => {
    // @mcc-dev-preview:start card-sell-dev-state
    if (devState) return;
    // @mcc-dev-preview:end card-sell-dev-state

    const validateCardStateAndLoadData = async () => {
      try {
        const cardId = finalCardId;
        
        if (!cardId) {
          const errorMessage = 'Card ID is required';
          navigate(CardStateService.getErrorRoute(errorMessage));
          return;
        }
        
        // Load card details
        const cardData = await ApiService.getCardByCardId(cardId);
        
        if (!cardData) {
          const errorMessage = `Card not found for ID: ${cardId}`;
          navigate(CardStateService.getErrorRoute(errorMessage, cardId));
          return;
        }
        
        // Check if card is activated (can be sold)
        if (cardData.state !== CardState.ACTIVATED) {
          const errorMessage = `Card is not activated. Current state: ${cardData.state}`;
          navigate(CardStateService.getErrorRoute(errorMessage, cardId));
          return;
        }

        // Load user details
        const userData = await ApiService.getUserForCard(cardId);

        const sellableResponse = await ApiService.getSellableBalance(cardId);
        if (sellableResponse.success && sellableResponse.data) {
          setMaxSellCrypto(sellableResponse.data.maxSellCrypto);
          setGasReserve(sellableResponse.data.gasReserve);
          setEstimationDisclaimer(
            sellableResponse.data.estimationDisclaimer ||
              'Maximum sell amount is estimated; the exact network fee is confirmed when you complete the transfer.'
          );
        } else {
          throw new Error(sellableResponse.error || 'Failed to load sellable balance');
        }
        
        // Load sell quotes
        const now = Date.now();
        let quotesData = null;
        
        // Check cache (30 second expiry)
        if (quoteCache && (now - quoteCache.timestamp) < 30000) {
          quotesData = quoteCache.data;
        } else {
          const quotesResponse = await ApiService.getCardValue(cardId);
          if (quotesResponse.success && quotesResponse.data) {
            quotesData = quotesResponse.data;
            setQuoteCache({ data: quotesData, timestamp: now });
          }
        }
        
        if (quotesData) {
          // Filter for payid-bank-transfer (only payment method)
          const filteredQuotes = quotesData.quotes.filter((quote: any) => 
            quote.paymentMethodId === 'payid-bank-transfer'
          );
          setSellQuotes(filteredQuotes);
        }
        
        setCardDetails(cardData);
        setUserDetails(userData.data.user);
        
        setIsValidatingState(false);
      } catch (error: any) {
        console.error('❌ CardSell: Error validating card state:', error);
        const errorMessage = 'Failed to verify card state';
        navigate(CardStateService.getErrorRoute(errorMessage, finalCardId || undefined));
      }
    };

    validateCardStateAndLoadData();
  }, [finalCardId, navigate, devState]);

  // Calculate amounts when sell percentage changes
  useEffect(() => {
    if (!cardDetails || !sellQuotes.length || sellPercentage === 0 || maxSellCrypto == null) {
      setCalculatedAmount({ crypto: '', aud: '', processingFee: '', networkFee: '' });
      return;
    }

    const sellAmountCrypto = maxSellCrypto * (sellPercentage / 100);

    const quote = sellQuotes.find((q: any) => q.paymentMethodId === 'payid-bank-transfer');
    if (!quote) {
      setCalculatedAmount({ crypto: '', aud: '', processingFee: '', networkFee: '' });
      return;
    }

    const cardBalance = parseFloat(String(cardDetails.balance)) || maxSellCrypto;
    const quoteScale = cardBalance > 0 ? maxSellCrypto / cardBalance : 1;
    const sellScale = (sellPercentage / 100) * quoteScale;

    const sellAmountAUD = (quote.fiatAmount || 0) * sellScale;
    const sellProcessingFee = (quote.processingFee || 0) * sellScale;
    const sellNetworkFee = (quote.networkFee || 0) * sellScale;

    setCalculatedAmount({ 
      crypto: sellAmountCrypto.toFixed(8), 
      aud: sellAmountAUD.toFixed(2),
      processingFee: sellProcessingFee.toFixed(2),
      networkFee: sellNetworkFee.toFixed(2)
    });
  }, [sellPercentage, cardDetails, sellQuotes, maxSellCrypto]);

  const handleSubmit = () => {
    // Validate sell percentage
    if (sellPercentage <= 0) {
      alert('Please select an amount to sell');
      return;
    }

    const sellAmountNum = parseFloat(calculatedAmount.crypto);
    const maxSell = maxSellCrypto ?? 0;

    if (sellAmountNum > maxSell + 1e-12) {
      alert('Sell amount cannot exceed your available balance (network fee reserved)');
      return;
    }

    // Validate we have a quote
    if (!sellQuotes.length) {
      alert('Unable to get sell quote. Please try again.');
      return;
    }

    // Show confirmation dialog
    setOpenDialog(true);
  };

  const handleContinue = async () => {
    setOpenDialog(false);
    setIsLoading(true);

    try {
      if (!finalCardId || !cardDetails || !sellQuotes.length) {
        throw new Error('Missing required data for sell order');
      }

      // Get quote for payid-bank-transfer
      const quote = sellQuotes.find((q: any) => q.paymentMethodId === 'payid-bank-transfer');
      if (!quote) {
        throw new Error('Sell quote not found');
      }

      // Create sell order
      const response = await ApiService.createSellOrder({
        cardId: finalCardId,
        cryptoAmount: parseFloat(calculatedAmount.crypto),
        fiatAmount: parseFloat(calculatedAmount.aud),
        fiat: quote.fiatCode || 'AUD',
        paymentMethodId: 'payid-bank-transfer',
        processingFee: parseFloat(calculatedAmount.processingFee),
        networkFee: parseFloat(calculatedAmount.networkFee)
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create sell order');
      }

      // Redirect to Banxa checkout URL
      window.location.href = response.data.checkoutUrl;
    } catch (error: any) {
      console.error('Error creating sell order:', error);
      setError(error.message || 'Failed to create sell order. Please try again.');
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    setOpenDialog(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Show loading state while validating card state
  if (isValidatingState) {
    return (
      <div className="card-sell-container ev-page ev-page-enter">
        <AppHeader />
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', padding: '40px 20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', textAlign: 'center' }}>
            <div className="spinner"></div>
            <p style={{ color: '#ffffff', fontSize: '16px', margin: 0, fontWeight: 500 }}>Loading card details...</p>
          </div>
        </main>
      </div>
    );
  }

  // Show sell loading state
  if (isLoading) {
    return (
      <div className="card-sell-container ev-page ev-page-enter">
        <AppHeader />
        <main className="card-sell-main">
          <div className="content-wrapper">
            <div className="sell-loading">
              <h2 className="sell-title">Processing Your Sale</h2>
              <p className="sell-message">Please wait while we process your crypto sale...</p>
              
              <div className="progress-section">
                <div className="progress-bar">
                  <div className="progress-fill"></div>
                </div>
                <div className="progress-text">Processing sale...</div>
              </div>

              <div className="sell-details">
                <div className="detail-item">
                  <span className="detail-label">Card ID:</span>
                  <span className="detail-value">{finalCardId}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Sell Amount:</span>
                  <span className="detail-value">{calculatedAmount.crypto} {cardDetails?.coin}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">AUD Amount:</span>
                  <span className="detail-value">${calculatedAmount.aud}</span>
                </div>
              </div>

              <div className="loading-animation">
                <div className="spinner"></div>
                <p>Processing your sale...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="card-sell-container ev-page ev-page-enter" data-testid="card-sell-page">
      <AppHeader />
      {/* Main Content */}
      <main className="card-sell-main">
        <div className="content-wrapper">
          <div className="page-header">
          <h1 className="main-heading">SELL CRYPTO</h1>
          <h2 className="main-subtitle">Convert your crypto to AUD</h2>
          </div>
          {/* Sell Form */}
          <div className="sell-form-container">
            <div className="card-sell-form">
              <div className="page-header">
              <h3 className="sell-form-heading">How much do you want to sell?</h3></div>
            <div className="sell-amount-section">
                <div className="slider-container">
                        <div className="page-header">
                  <Typography
                    variant="h3"
                    className="slider-label"
                    sx={{
                      mb: 2,
                      pb: 0,
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    SELECT AMOUNT: {sellPercentage}%
                  </Typography>
                  </div>
                  <Slider
                    value={sellPercentage}
                    onChange={(_, value) => setSellPercentage(value as number)}
                    min={0}
                    max={100}
                    step={1}
                    valueLabelDisplay="auto"
                  />

                  {estimationDisclaimer && (
                    <Typography
                      variant="body2"
                      className="sell-estimate-disclaimer"
                      sx={{ mt: 1.5, fontSize: '12px', color: '#f9f9f9', textAlign: 'center' }}
                    >
                      {estimationDisclaimer}
                    </Typography>
                  )}
                  
                  {calculatedAmount.crypto && calculatedAmount.aud && (
                    <div className="calculated-amounts">
                      <div className="calculated-item">
                        <span>You will sell:</span>
                        <span className="calculated-value">{calculatedAmount.crypto} {cardDetails?.coin}</span>
                      </div>
                      <div className="calculated-item">
                        <span>You will receive:</span>
                        <span className="calculated-value">${calculatedAmount.aud} AUD</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="card-sell-buttons">
              <button
                  type="button"
                  className="submit-button"
                  onClick={handleSubmit}
                  disabled={sellPercentage <= 0}
                >
                  Sell Crypto
                </button>
                <button
                  type="button"
                  className="back-button"
                  onClick={handleBack}
                  aria-label="Go back"
                >
                  <img src="/back-icon.svg" alt="" className="back-button-icon" aria-hidden="true" />
                  <span>Back</span>
                </button>                
              </div>
            </div>
          </div>
        </div>
      </main>

      <Dialog
        open={openDialog}
        onClose={isLoading ? undefined : handleGoBack}
        PaperProps={{
          sx: {
            background: 'rgba(10, 10, 10, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
            color: '#ffffff'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', color: '#ffffff' }}>
          Confirm Sale
        </DialogTitle>
        <DialogContent>
          <div style={{ textAlign: 'center', color: '#ffffff' }}>
            <p>You are about to sell:</p>
            <p><strong>{calculatedAmount.crypto} {cardDetails?.coin}</strong></p>
            <p>You will receive:</p>
            <p><strong>${calculatedAmount.aud} AUD</strong></p>
            <p>Funds will be transferred to your chosen bank account. Please note the bank account details must <strong>match the details provided during your card activation</strong></p>
            <p style={{ fontSize: '0.9rem', color: '#cccccc', marginTop: '1rem' }}>
              This action cannot be undone. You will complete the crypto transfer with your PIN after returning from Banxa.
            </p>
          </div>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button
            onClick={handleGoBack}
            variant="outlined"
            disabled={isLoading}
            sx={{
              color: '#ffffff',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.4)',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            variant="contained"
            disabled={isLoading}
            sx={{
              background: 'var(--ev-btn-primary)',
              color: '#ffffff',
              fontWeight: 'bold',
              '&:hover': {
                background: 'var(--ev-btn-primary-hover)',
              }
            }}
          >
            {isLoading ? 'Processing...' : 'Confirm Sale'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CardSell;
